import { create } from 'zustand';

import { RingChannel } from './channel.js';
import type { HudEvent, HudSnapshot } from './types.js';

/** Snapshot published at 20 Hz; React re-renders the panels from it. */
export interface HudState {
  snapshot: HudSnapshot | null;
  publish: (snapshot: HudSnapshot | null) => void;
}

export const useHudStore = create<HudState>((set) => ({
  snapshot: null,
  publish: (snapshot) => set({ snapshot }),
}));

/** One-shot events (hit markers, damage numbers, feed) drained by the HUD's animation frame. */
export const hudEvents = new RingChannel<HudEvent>(256);

/**
 * Per-frame values written by the game loop and read by DOM refs (never React state): the
 * enemy nameplate's screen position, the crosshair spread, the low-health vignette strength.
 */
export interface HudFrame {
  nameplate: { x: number; y: number; visible: boolean; distance: number };
  crosshairSpread: number;
  lowHp: number;
  aimingAtEnemy: boolean;
  /** The scoreboard key is held (Phase 8). */
  scoreboard: boolean;
  /** The kill cam is showing this pilot's view; null otherwise (Phase 8). */
  killcam: string | null;
}

export const hudFrame: HudFrame = {
  nameplate: { x: 0, y: 0, visible: false, distance: 0 },
  crosshairSpread: 0,
  lowHp: 0,
  aimingAtEnemy: false,
  scoreboard: false,
  killcam: null,
};

export function resetHud(): void {
  useHudStore.getState().publish(null);
  hudEvents.clear();
  hudFrame.nameplate.visible = false;
  hudFrame.crosshairSpread = 0;
  hudFrame.lowHp = 0;
  hudFrame.aimingAtEnemy = false;
  hudFrame.scoreboard = false;
  hudFrame.killcam = null;
}
